import {
  loadGroupsFromCache,
  saveGroupsToCache,
} from 'features/members/hooks/useMembersData/useMembersData';
import { IMemberGroup } from 'features/members/models';
import { normalizeMemberId, toPrefixedMemberId } from './memberId';

type CacheKey = string;

type MemberIdVariants = {
  raw: string;
  normalized: string;
  prefixed: string;
};

const getMemberIdVariants = (id: string): MemberIdVariants => ({
  raw: id,
  normalized: normalizeMemberId(id),
  prefixed: toPrefixedMemberId(id),
});

const uniqById = <T extends { id: string }>(items: T[]) => {
  const map = new Map<string, T>();
  items.forEach((item) => map.set(item.id, item));
  return Array.from(map.values());
};

const applyToAllMemberIdVariants = (
  map: Map<string, IMemberGroup[]>,
  variants: MemberIdVariants,
  updater: (existing: IMemberGroup[]) => IMemberGroup[],
) => {
  const keys = [variants.raw, variants.normalized, variants.prefixed].filter(
    Boolean,
  );
  keys.forEach((key) => {
    const existing = map.get(key) ?? [];
    map.set(key, updater(existing));
  });
};

export const addMemberTeamsToCache = (args: {
  organizationId: string | null;
  memberId: string;
  teamIds: string[];
  groupNameById?: Map<string, string>;
}) => {
  const { organizationId, memberId, teamIds, groupNameById } = args;
  if (!memberId || teamIds.length === 0) return;

  const cacheKey: CacheKey = organizationId || 'default';
  const memberIdVariants = getMemberIdVariants(memberId);

  const map: Map<string, IMemberGroup[]> =
    loadGroupsFromCache(cacheKey) ?? new Map<string, IMemberGroup[]>();

  const addedGroups: IMemberGroup[] = teamIds.map((id) => ({
    id,
    name: groupNameById?.get(id) ?? '',
  }));

  applyToAllMemberIdVariants(map, memberIdVariants, (existing) =>
    uniqById([...existing, ...addedGroups]),
  );

  saveGroupsToCache(map, cacheKey);
  window.dispatchEvent(
    new CustomEvent('memberGroupsCacheCleared', {
      detail: {
        organizationId: cacheKey,
        memberId,
        addedTeamIds: [...teamIds],
      },
    }),
  );
};

export const removeMemberTeamFromCache = (args: {
  organizationId: string | null;
  memberId: string;
  teamId: string;
}) => {
  const { organizationId, memberId, teamId } = args;
  if (!memberId || !teamId) return;

  const cacheKey: CacheKey = organizationId || 'default';
  const memberIdVariants = getMemberIdVariants(memberId);

  const map: Map<string, IMemberGroup[]> =
    loadGroupsFromCache(cacheKey) ?? new Map<string, IMemberGroup[]>();

  applyToAllMemberIdVariants(map, memberIdVariants, (existing) =>
    existing.filter((g) => g.id !== teamId),
  );

  saveGroupsToCache(map, cacheKey);
  window.dispatchEvent(
    new CustomEvent('memberGroupsCacheCleared', {
      detail: {
        organizationId: cacheKey,
        memberId,
        removedTeamId: teamId,
      },
    }),
  );
};
